<template>
  <div>
    <v-alert v-if="!usuarioLogado.adminWeb" type="warning" outlined dense class="mb-4">
      Acesso restrito: faça login como administrador para gerenciar estagiários do magistério.
    </v-alert>
    <v-container v-else fluid class="estagiarios-container">
      <v-row justify="center">
        <v-col cols="12">
          <v-card class="pa-5 estagiarios-card">
            <v-card-title class="font-weight-bold">Estagiários do Magistério</v-card-title>
            <v-form ref="form" v-model="valid" @submit.prevent="salvar">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field v-model="form.nome" label="Nome" outlined dense :rules="[(v) => !!v || 'Informe o nome']"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.cpf"
                    label="CPF"
                    outlined
                    dense
                    maxlength="14"
                    placeholder="000.000.000-00"
                    @input="formatarCpf"
                    :rules="[(v) => !!v || 'Informe o CPF']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.email"
                    label="Email"
                    outlined
                    dense
                    type="email"
                    :rules="[(v) => !!v || 'Informe o email']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="8">
                  <v-select
                    v-model="form.fkescola"
                    :items="escolas"
                    item-text="descricao"
                    item-value="pkescola"
                    label="Instituição"
                    outlined
                    dense
                    :rules="[(v) => !!v || 'Selecione a instituição']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn color="success" block :loading="salvando" @click="salvar">Salvar</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12">
          <v-card class="mt-6" elevation="2">
            <v-card-title class="font-weight-bold d-flex align-center">
              Estagiários Cadastrados
              <v-spacer></v-spacer>
              <v-btn icon :loading="loading" title="Atualizar lista" @click="carregarEstagiarios">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
            <v-data-table :headers="headers" :items="estagiarios" :loading="loading" no-data-text="Nenhum estagiário cadastrado.">
              <template v-slot:item.acoes="{ item }">
                <v-icon color="error" title="Excluir" @click="excluir(item)">mdi-delete</v-icon>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EscolaServices from '@/services/base/EscolaServices';
import EstagiariosMagisterioServices from '@/services/ava/EstagiariosMagisterioServices';
import filters from '@/libs/filters';

export default {
  name: 'EstagiariosMagisterio',
  data: () => ({
    loading: false,
    salvando: false,
    valid: true,
    escolas: [],
    estagiarios: [],
    form: {
      nome: '',
      cpf: '',
      email: '',
      fkescola: null,
    },
    headers: [
      { text: 'Nome', value: 'nome' },
      { text: 'CPF', value: 'cpf' },
      { text: 'Email', value: 'email' },
      { text: 'Instituição', value: 'instituicao' },
      { text: 'Ações', value: 'acoes', sortable: false },
    ],
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),
  computed: {
    ...mapGetters({
      usuarioLogado: 'config/getUsuarioLogado',
    }),
  },
  mounted() {
    this.carregarEscolas();
    this.carregarEstagiarios();
  },
  methods: {
    formatarCpf() {
      const valor = this.form.cpf.replace(/\D/g, '');
      if (valor.length <= 11) {
        this.form.cpf = filters.filterCpf(valor);
      }
    },
    async carregarEscolas() {
      try {
        const data = await EscolaServices.load({ fkadministracao: 1 });

        this.escolas = data || [];
      } catch (error) {
        this.snackbarText = 'Erro ao carregar instituições.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },
    async carregarEstagiarios() {
      this.loading = true;
      try {
        const data = await EstagiariosMagisterioServices.load();

        this.estagiarios = data || [];
      } catch (error) {
        this.snackbarText = 'Erro ao carregar estagiários.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },
    async excluir(item) {
      if (!confirm(`Deseja excluir o estagiário "${item.nome}"?`)) return;
      this.loading = true;
      try {
        await EstagiariosMagisterioServices.remove(item.fkpessoa);
        this.snackbarText = 'Estagiário excluído com sucesso!';
        this.snackbarColor = 'success';
        await this.carregarEstagiarios();
      } catch (error) {
        this.snackbarText = error?.response?.data?.error || error?.message || 'Erro ao excluir estagiário.';
        this.snackbarColor = 'error';
        this.loading = false;
      } finally {
        this.snackbar = true;
      }
    },
    async salvar() {
      if (!this.$refs.form.validate()) return;
      this.salvando = true;
      try {
        const payload = {
          nome: this.form.nome,
          email: this.form.email,
          cpf: this.form.cpf.replace(/\D/g, ''),
          fkescola: this.form.fkescola,
        };
        const data = await EstagiariosMagisterioServices.insert(payload);
        console.log(payload, data);
        if (data?.success) {
          this.snackbarText = 'Estagiário cadastrado com sucesso!';
          this.snackbarColor = 'success';
          this.form = { nome: '', cpf: '', email: '', fkescola: null };
          this.$refs.form.resetValidation();
          await this.carregarEstagiarios();
        } else {
          this.snackbarText = data?.message || 'Não foi possível cadastrar o estagiário.';
          this.snackbarColor = 'warning';
        }
      } catch (error) {
        this.snackbarText = error?.response?.data?.error || error?.message || 'Erro ao cadastrar estagiário.';
        this.snackbarColor = 'error';
      } finally {
        this.snackbar = true;
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.estagiarios-container {
  padding: 16px;
}

.estagiarios-card {
  width: 100%;
}

@media (min-width: 1264px) {
  .estagiarios-container {
    padding-left: 40px;
    padding-right: 40px;
  }
}
</style>
